import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Coffee,
  UtensilsCrossed,
  HandPlatter,
  ShoppingBasket,
  Smile,
  TimerReset,
  Frown,
  Sparkles,
  Zap,
  Settings2,
  Gem,
} from 'lucide-react';
import useSwipeToClose from '../../../hooks/useSwipeToClose';

// Single meal-type card (3 breakfasts / 3 lunches / 3 dinners)
function MealTypeCard({ Icon, label }) {
  return (
    <div className="flex-1 flex items-start bg-white border-[0.5px] border-[#56554d] rounded-[12px] pt-3 pb-[10px] px-3">
      <div className="flex-1 flex flex-col items-center gap-2">
        <div className="flex flex-col items-center gap-1">
          <Icon size={20} strokeWidth={1.75} className="text-[#0d0c07]" />
          <p className="text-[14px] leading-[24px] text-[#0d0c07] whitespace-nowrap">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
}

// Icon + text row (bullet style with gray icon background)
function IconBulletRow({ Icon, children, textSize = '15.2px', textLeading = '28px' }) {
  return (
    <div className="flex gap-2 items-start w-full">
      <div className="flex flex-col items-center justify-center bg-[#f3f3f2] rounded-[6px] w-7 h-7 px-2 py-1 shrink-0">
        <Icon size={20} strokeWidth={1.75} className="text-[#0d0c07]" />
      </div>
      <p
        className="flex-1 text-[#0d0c07]"
        style={{ fontSize: textSize, lineHeight: textLeading }}
      >
        {children}
      </p>
    </div>
  );
}

function WhyNineMealsDrawer({ isOpen, onClose }) {
  const { t } = useTranslation('wizard');
  const { containerRef, dragOffset, shouldRender, isVisible, DragHandle } =
    useSwipeToClose(onClose, isOpen);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 z-[200] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#f8f8f7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden"
        style={{
          maxHeight: 'calc(100dvh - 44px - env(safe-area-inset-top, 0px))',
          height: 'auto',
          transform: isVisible
            ? `translateY(${Math.max(0, dragOffset)}px)`
            : 'translateY(100%)',
          transition: dragOffset === 0
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-9 h-9 rounded-[12px] transition-colors z-20"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden px-6 pt-8 pb-[116px]">
          {/* Section 1: Budget intro */}
          <h2 className="text-[15.2px] leading-[28px] font-bold text-[#0d0c07] pb-3">
            {t('whyNineMeals.budgetTitle')}
          </h2>

          <div className="flex flex-col gap-4 pb-7">
            {/* Three meal-type cards */}
            <div className="flex gap-3 w-full">
              <MealTypeCard Icon={Coffee} label={t('whyNineMeals.breakfasts')} />
              <MealTypeCard Icon={UtensilsCrossed} label={t('whyNineMeals.lunches')} />
              <MealTypeCard Icon={HandPlatter} label={t('whyNineMeals.dinners')} />
            </div>
            {/* Yellow card with timer-reset icon */}
            <div className="flex gap-3 items-start bg-[#FFCB00] border-[0.5px] rounded-[12px] px-4 py-3">
              <div className="flex flex-col items-center justify-center h-7 py-1 shrink-0">
                <ShoppingBasket size={20} strokeWidth={1.75} className="text-[#0d0c07]" />
              </div>
              <p className="flex-1 text-[15.2px] leading-[28px] text-[#0d0c07]">
                {t('whyNineMeals.bullet1')}
              </p>
            </div>
          </div>

          {/* Section 2: Why 9 meals? */}
          <div className="flex flex-col gap-4 pb-7">
            <h3 className="text-[15.2px] leading-[28px] font-bold text-[#0d0c07]">
              {t('whyNineMeals.whyTitle')}
            </h3>

            {/* Yellow card with timer-reset icon */}
            <div className="flex gap-3 items-start bg-[#FFCB00] border-[0.5px] rounded-[12px] px-4 py-3">
              <div className="flex flex-col items-center justify-center h-7 py-1 shrink-0">
                <TimerReset size={20} strokeWidth={1.75} className="text-[#0d0c07]" />
              </div>
              <p className="flex-1 text-[15.2px] leading-[28px] text-[#0d0c07]">
                {t('whyNineMeals.cookWindowPart1')}
                <span className="font-bold">{t('whyNineMeals.cookWindowBold')}</span>
                {t('whyNineMeals.cookWindowPart2')}
              </p>
            </div>

            {/* White card without icon (rounded-tr smaller per Figma) */}
            <div className="bg-white border-[0.5px] border-[#e2e1df] rounded-[12px] px-4 pt-4 pb-5">
              <p className="text-[15.2px] leading-[28px] font-bold text-[#0d0c07]">
                {t('whyNineMeals.skipDayBold')}
              </p>
              <p className="text-[14px] leading-[24px] text-[#006600] mt-1">
                {t('whyNineMeals.skipDayDesc')}
              </p>
            </div>
          </div>

          {/* Section 3: Why is this better? */}
          <div className="flex flex-col gap-4 pb-6">
            <h3 className="text-[15.2px] leading-[28px] font-bold text-[#0d0c07]">
              {t('whyNineMeals.betterTitle')}
            </h3>

            {/* Section 4: Yellow positioning statement */}
            <div className="flex gap-3 items-start bg-[#FFCB00] border-[0.5px] rounded-[12px] px-4 py-3">
              <div className="flex flex-col items-center justify-center h-7 py-1 shrink-0">
                <Gem size={20} strokeWidth={1.75} className="text-[#0d0c07]" />
              </div>
              <p className="flex-1 text-[15.2px] leading-[28px] text-[#0d0c07]">
                {t('whyNineMeals.positioning')}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <IconBulletRow Icon={Frown} textSize="15.2px" textLeading="28px">
                {t('whyNineMeals.better1')}
              </IconBulletRow>
              <IconBulletRow Icon={Sparkles} textSize="15.2px" textLeading="28px">
                {t('whyNineMeals.better2')}
              </IconBulletRow>
              {/*<IconBulletRow Icon={Zap} textSize="15.2px" textLeading="28px">
                {t('whyNineMeals.better3')}
              </IconBulletRow>*/}
              <IconBulletRow Icon={Settings2} textSize="15.2px" textLeading="28px">
                {t('whyNineMeals.better4')}
              </IconBulletRow>
              <IconBulletRow Icon={Smile}>
              {t('whyNineMeals.bullet2')}
            </IconBulletRow>
            </div>
          </div>

          
        </div>

        {/* Fixed bottom CTA */}
        <div className="fixed bottom-0 left-0 right-0 pt-2 pb-5 px-0 flex justify-center bg-white z-10 rounded-[12px_12px_0px_0px] shadow-[0px_0px_1px_0px_rgba(13,12,7,0.12),_0px_-4px_24px_0px_rgba(13,12,7,0.12),_1px_-2px_8px_0px_rgba(13,12,7,0.08)]">
          <div className="w-full max-w-5xl px-6 flex items-center justify-between">
            <button
              type="button"
              className="inline-flex justify-center items-center gap-1 h-[56px] px-6 py-[14px] rounded-[12px] border-[0.5px] border-[#060] bg-[#060] text-white text-center text-[15.2px] font-semibold leading-[28px] transition-colors md:hover:border-[#090] md:hover:bg-[#090] w-full"
              onClick={onClose}
            >
              {t('whyNineMeals.gotIt')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default WhyNineMealsDrawer;
